import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAllStudents,
  createMiscBill,
  getCategory,
  getAllPredefinedMiscItems,
  getAdmissions,
} from "../../api/Api"; // Import getCategory
import Setting from "../settings/PredefinedMiscItems.jsx";
import { AuthContext } from "../../utils/AuthContext";
import { useContext } from "react";
import { ArrowLeft } from "lucide-react";

const AddMiscBill = ({ onBack }) => {
  const { selectedBranchId } = useContext(AuthContext);
  const [query, setQuery] = useState("");
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [items, setItems] = useState([
    { description: "", amount: "", quantity: 1 },
  ]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]); // State for categories
  const [selectedCategory, setSelectedCategory] = useState(""); // State for selected category
  const [predefinedMiscItems, setPredefinedMiscItems] = useState([]);
  const [selectedPredefinedItem, setSelectedPredefinedItem] = useState("");

  // New states for voucher-like fields
  const [payeeName, setPayeeName] = useState("");
  const [payeePhoneNumber, setPayeePhoneNumber] = useState("");
  const [particulars, setParticulars] = useState("");
  const [modeOfPayment, setModeOfPayment] = useState(""); // State for mode of payment
  const [transactionId, setTransactionId] = useState(""); // New state for Transaction ID

  // New states for Amount in Words and Accountant Name
  const [amountInWords, setAmountInWords] = useState("");
  const [accountantName, setAccountantName] = useState("");
  const [billDate, setBillDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  // Function to convert number to words (reused from AddExpansive.jsx)
  const numberToWords = (num) => {
    const ones = [
      "",
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
    ];
    const teens = [
      "ten",
      "eleven",
      "twelve",
      "thirteen",
      "fourteen",
      "fifteen",
      "sixteen",
      "seventeen",
      "eighteen",
      "nineteen",
    ];
    const tens = [
      "",
      "",
      "twenty",
      "thirty",
      "forty",
      "fifty",
      "sixty",
      "seventy",
      "eighty",
      "ninety",
    ];

    const getTwoDigits = (n) => {
      if (n < 10) return ones[n];
      else if (n < 20) return teens[n - 10];
      else
        return (
          tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "")
        );
    };

    const getThreeDigits = (n) => {
      if (n === 0) return "";
      const hundred = Math.floor(n / 100);
      const rest = n % 100;
      return (
        (hundred ? ones[hundred] + " hundred" + (rest ? " and " : "") : "") +
        getTwoDigits(rest)
      );
    };

    if (num === 0) return "zero";

    let result = "";

    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor(num % 1000);

    if (crore) result += getTwoDigits(crore) + " crore ";
    if (lakh) result += getTwoDigits(lakh) + " lakh ";
    if (thousand) result += getTwoDigits(thousand) + " thousand ";
    if (hundred) result += getThreeDigits(hundred);

    return result.trim();
  };

  const fetchPredefinedItems = async () => {
    try {
      const response = await getAllPredefinedMiscItems();
      if (response && response.data) {
        setPredefinedMiscItems(response.data);
      }
    } catch (err) {
      console.error("Error fetching predefined items:", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const studentsResponse = await getAdmissions({ all: true });

        const responseData = studentsResponse.data || [];
        // console.log("Fetched Students:", studentsResponse);

        const studentsData = responseData.map((s) => ({
          ...s,
          studentName: s.particularsOfChild?.fullName,
          fatherName: s.particularsOfParents?.FatherName,
        }));
        setStudents(studentsData);
        console.log("Processed Students:", studentsData);

        const categoriesResponse = await getCategory();
        if (categoriesResponse && categoriesResponse.data) {
          setCategories(categoriesResponse.data);
        }

        await fetchPredefinedItems();
      } catch (err) {
        console.error("Error fetching data:", err);
        toast.error("Failed to load necessary data.");
      }
    };
    fetchData();
  }, []);

  // Clear transactionId if modeOfPayment changes to Cash
  useEffect(() => {
    if (modeOfPayment === "Cash") {
      setTransactionId("");
    }
  }, [modeOfPayment]);

  // Update amount in words when totalAmount changes
  useEffect(() => {
    setAmountInWords(numberToWords(totalAmount));
  }, [totalAmount]);

  const handleSearchChange = (e) => {
    const val = e.target.value.trim();
    setQuery(val);
    console.log(students)
    if (!val) {
      setFilteredStudents(students); // show all when empty
      return;
    }

    const filtered = students.filter((student) =>
      student?.studentName
        ?.toLowerCase()
        .includes(val.toLowerCase())
    );
    // consoel.log(filtered)

    setFilteredStudents(filtered);
  };


  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    console.log("Selected Student:", student);
    setQuery(student.studentName);
    setFilteredStudents([]);
  };

  const handleItemChange = (index, event) => {
    const { name, value } = event.target;
    const newItems = [...items];
    newItems[index][name] =
      name === "quantity" || name === "amount" ? parseFloat(value) || 0 : value;
    setItems(newItems);
    calculateTotal(newItems);
  };

  const handleAddItem = () => {
    setItems([...items, { description: "", amount: "", quantity: 1 }]);
  };

  const handleAddPredefinedItem = () => {
    if (selectedPredefinedItem) {
      const item = predefinedMiscItems.find(
        (p) => p._id === selectedPredefinedItem,
      );
      if (item) {
        const newItems = [
          ...items,
          { description: item.name, amount: item.amount, quantity: 1 },
        ];
        setItems(newItems);
        calculateTotal(newItems);
      }
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    calculateTotal(newItems);
  };

  const calculateTotal = (currentItems) => {
    const total = currentItems.reduce((sum, item) => {
      return (
        sum + (parseFloat(item.amount) || 0) * (parseFloat(item.quantity) || 0)
      );
    }, 0);
    setTotalAmount(total);
  };

  const handleSubmit = async () => {
    if (!selectedStudent) {
      toast.error("Please select a student.");
      return;
    }
    if (!selectedCategory) {
      toast.error("Please select a category.");
      return;
    }
    if (
      items.some(
        (item) => !item.description || !item.amount || item.quantity <= 0,
      )
    ) {
      toast.error(
        "Please ensure all item descriptions and amounts are filled, and quantity is greater than 0.",
      );
      return;
    }
    if (totalAmount <= 0) {
      toast.error("Total amount must be greater than zero.");
      return;
    }
    if (
      !payeeName ||
      !payeePhoneNumber ||
      !particulars ||
      !accountantName ||
      !amountInWords
    ) {
      toast.error(
        "Please fill in all required details (Payee, Phone, Particulars, Accountant Name, Amount in Words).",
      );
      return;
    }
    if (!modeOfPayment) {
      toast.error("Please select a mode of payment.");
      return;
    }
    if (modeOfPayment !== "Cash" && !transactionId) {
      toast.error(
        "Please enter a Transaction ID for the selected payment method.",
      );
      return;
    }

    setLoading(true);
    try {
      const billData = {
        studentId: selectedStudent._id,
        items: items.map((item) => ({
          description: item.description,
          amount: parseFloat(item.amount),
          quantity: parseInt(item.quantity),
        })),
        totalAmount: totalAmount,
        amountInWords: amountInWords, // Added
        accountantName: accountantName, // Added
        status: "paid", // Default status
        category: selectedCategory,
        payeeName,
        payeePhoneNumber,
        date: billDate,
        particulars,
        modeOfPayment,
        academicYear: selectedStudent.academicYear,
        transactionId: modeOfPayment !== "Cash" ? transactionId : undefined, // Conditionally add transactionId
        branch: selectedBranchId || localStorage.getItem("selectedBranchId"),
      };
      console.log("Submitting misc bill with branchId:", billData.branch);
      if (!billData.branch) {
        toast.error("Branch ID is missing. Please re-select your branch.");
        return;
      }
      await createMiscBill(billData);
      toast.success("Miscellaneous bill created successfully!");
      // Reset form
      setQuery("");
      setSelectedStudent(null);
      setItems([{ description: "", amount: "", quantity: 1 }]);
      setTotalAmount(0);
      setSelectedCategory("");
      setPayeeName("");
      setPayeePhoneNumber("");
      setParticulars("");
      setAmountInWords("");
      setAccountantName("");
      setModeOfPayment("");
      setTransactionId(""); // Reset transactionId
      onBack();
    } catch (err) {
      console.error("Error creating miscellaneous bill:", err);
      toast.error("Failed to create miscellaneous bill.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto bg-white shadow-md rounded-lg">
      <ToastContainer />

      <div className="flex items-center mb-6">
        <button
          type="button"
          onClick={onBack}
          title="Back"
          aria-label="Back"
          className="mr-4 p-2 text-gray-700 rounded-lg hover:bg-gray-100 transition"
        >
          <ArrowLeft size={20} />
        </button>
      </div>

      {/* 🔍 Search Box */}
      <div className="relative mb-6">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Search for student by name or father's name..."
          value={query}
          onChange={handleSearchChange}
        />
        {filteredStudents.length > 0 && query && (
          <ul className="absolute z-10 w-full bg-white border border-gray-200 mt-1 rounded-md shadow max-h-60 overflow-y-auto">
            {filteredStudents.map((student) => (
              <li
                key={student._id}
                onClick={() => handleSelectStudent(student)}
                className="px-4 py-2 hover:bg-blue-100 cursor-pointer transition"
              >
                <span className="font-semibold text-blue-700">
                  {student.studentName}
                </span>
                {' - '}
                <span className="text-gray-500">{student.fatherName}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedStudent && (
        <>
          <h2 className="text-xl font-bold mb-6 text-[#16bab1]">💰 Add Cash Receipt</h2>
          <div className="mb-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Selected Student
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <p>
                <strong>Name:</strong> {selectedStudent.studentName}
              </p>
              <p>
                <strong>Father's Name:</strong> {selectedStudent.fatherName}
              </p>
              <p>
                <strong>Class:</strong> {selectedStudent.admissionFor || "N/A"}
              </p>
              <p>
                <strong>Academic Year:</strong> {selectedStudent.academicYear || "N/A"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-[2fr_1fr] gap-4">
            {/* Left Column */}
            <div>
              {/* Payee Name */}
              <div className="flex space-x-1 items-center mb-4">
                <label className="bg-[#16bab1] text-white text-nowrap w-44 px-2 py-1">Payee Name:</label>
                <input
                  type="text"
                  value={payeeName}
                  onChange={(e) => setPayeeName(e.target.value)}
                  placeholder="Name"
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>

              {/* Phone Number */}
              <div className="flex space-x-1 items-center mb-4">
                <label className="bg-[#16bab1] text-white text-nowrap w-44 px-2 py-1">Phone Number:</label>
                <input
                  type="tel"
                  value={payeePhoneNumber}
                  onChange={(e) => setPayeePhoneNumber(e.target.value)}
                  placeholder="Phone"
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>

              {/* Date */}
              <div className="flex space-x-1 items-center mb-4">
                <label className="bg-[#16bab1] text-white text-nowrap w-44 px-2 py-1">Date:</label>
                <input
                  type="date"
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value)}
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>

              {/* Particulars */}
              <div className="flex space-x-1 items-center mb-4">
                <label className="bg-[#16bab1] text-white text-nowrap w-44 px-2 py-1">Particulars:</label>
                <input
                  type="text"
                  value={particulars}
                  onChange={(e) => setParticulars(e.target.value)}
                  placeholder="Details"
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>

              {/* Accountant Name */}
              <div className="flex space-x-1 items-center mb-4">
                <label className="bg-[#16bab1] text-white text-nowrap w-44 px-2 py-1">Accountant Name:</label>
                <input
                  type="text"
                  value={accountantName}
                  onChange={(e) => setAccountantName(e.target.value)}
                  placeholder="Accountant"
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>
            </div>

            {/* Right Column */}
            <div>
              {/* Category */}
              <div className="mb-4 flex flex-col">
                <label className="bg-[#16bab1] text-white w-full px-2 py-1">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="bg-gray-200 outline-none px-2 py-1 flex-1"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mode of Payment */}
              <div className="mb-4 flex flex-col">
                <label className="bg-[#16bab1] text-white w-full px-2 py-1">Mode of Payment</label>
                <select
                  value={modeOfPayment}
                  onChange={(e) => setModeOfPayment(e.target.value)}
                  className="bg-gray-200 outline-none px-2 py-1 flex-1"
                  required
                >
                  <option value="">Select Mode</option>
                  <option value="Cash">Cash</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Online Transfer">Online Transfer</option>
                </select>
              </div>

              {/* Transaction ID */}
              {modeOfPayment !== "Cash" && modeOfPayment !== "" && (
                <div className="mb-4 flex flex-col">
                  <label className="bg-[#16bab1] text-white w-full px-2 py-1">Transaction ID</label>
                  <input
                    type="text"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="Transaction ID"
                    className="bg-gray-200 outline-none px-2 py-1 flex-1"
                    required
                  />
                </div>
              )}
            </div>
          </div>

          <hr className="my-6 border-gray-200" />

          {/* Bill Items Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Bill Items</h3>

              {/* Predefined Items Selector */}
              <div className="flex gap-2">
                <select
                  value={selectedPredefinedItem}
                  onChange={(e) => setSelectedPredefinedItem(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 text-sm max-w-xs"
                >
                  <option value="">Select predefined item</option>
                  {predefinedMiscItems.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name} - ₹{item.amount}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddPredefinedItem}
                  className="bg-green-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-green-600 transition"
                >
                  Add
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end"
                >
                  <div className="md:col-span-6">
                    <label className="block text-xs text-gray-500 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      name="description"
                      className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#16bab1]"
                      placeholder="Item Description"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, e)}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs text-gray-500 mb-1">
                      Amount
                    </label>
                    <input
                      type="number"
                      name="amount"
                      className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#16bab1]"
                      placeholder="Amount"
                      value={item.amount}
                      onChange={(e) => handleItemChange(index, e)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Qty
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#16bab1]"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, e)}
                      min="1"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <button
                      onClick={() => handleRemoveItem(index)}
                      className="w-full bg-red-100 text-red-600 p-2 rounded-lg hover:bg-red-200 transition flex justify-center items-center h-[42px]"
                      disabled={
                        items.length === 1 &&
                        item.description === "" &&
                        item.amount === "" &&
                        item.quantity === 1
                      }
                      title="Remove Item"
                    >
                      X
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleAddItem}
              className="mt-4 text-[#16bab1] font-medium hover:underline text-sm"
            >
              + Add Another Item
            </button>
          </div>

          {/* Totals Section using grid to align right */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pt-4 border-t border-gray-100">
            <div>
              <label className="block mb-1 font-medium">Amount in Words</label>
              <input
                type="text"
                value={amountInWords}
                onChange={(e) => setAmountInWords(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2 bg-gray-50 capitalize"
                readOnly
              />
            </div>
            <div className="flex flex-col justify-center items-end">
              <p className="text-gray-600 text-sm mb-1">Total Amount</p>
              <p className="text-3xl font-bold text-[#16bab1]">
                ₹{totalAmount.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="col-span-full flex flex-row gap-3 justify-end">
            <button
              onClick={handleSubmit}
              className="w-1/4 bg-[#16bab1] hover:bg-[#139e97] text-white font-semibold rounded-lg p-3 transition duration-200"
              disabled={loading || !selectedStudent}
            >
              {loading ? "Submitting..." : "Submit Bill"}
            </button>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="w-1/4 bg-[#F36743] hover:bg-[#139e97] text-white font-semibold rounded-lg p-3 transition duration-200"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {selectedStudent && (
        <div className="p-6 mt-6 bg-white shadow-md rounded-lg">
          <Setting onUpdate={fetchPredefinedItems} />
        </div>
      )}
    </div>
  );
};

export default AddMiscBill;
